import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import { esperar } from '@/shared/utils/esperar';

import type {
  CancelFlowBuilderTurnInput,
  CheckFlowBuilderTurnEndInput,
  CreateFlowBuilderSessionInput,
  FlowBuilderAgentFlowPayload,
  FlowBuilderCancelResult,
  FlowBuilderMessagePage,
  FlowBuilderMessagesQuery,
  FlowBuilderSessionDto,
  FlowBuilderSessionOpenedPayload,
  FlowBuilderSessionPage,
  FlowBuilderSessionsQuery,
  FlowBuilderStreamEventReceivedPayload,
  FlowBuilderTurnStatusDto,
  FlowBuilderTurnStreamOutcome,
  ResumeFlowBuilderTurnInput,
  StartFlowBuilderTurnInput,
} from '../../types/flowBuilder/flowBuilderApi';
import type {
  FlowBuilderDispatch,
  FlowBuilderThunkConfig,
  FlowBuilderTurnStreamArgs,
} from '../../types/flowBuilder/flowBuilderStore';
import { isLiveTurnStatus } from '../../utils/flowBuilder/flowBuilderTurnStatus';
import { toAgentProblem } from '../../utils/negotiationAgents/apiResponse';
import { fetchNegotiationAgent } from '../negotiationAgents/actions';
import { flowBuilderService } from './services';

export const flowBuilderStreamEventReceived = createAction<FlowBuilderStreamEventReceivedPayload>(
  'flowBuilder/streamEventReceived',
);
export const flowBuilderSessionOpened = createAction<FlowBuilderSessionOpenedPayload>(
  'flowBuilder/sessionOpened',
);
export const flowBuilderNewSessionStarted = createAction<{ agentId: string }>(
  'flowBuilder/newSessionStarted',
);
/** O editor do fluxo já recarregou do servidor: a pré-visualização dele pode sair. */
export const flowBuilderPreviewCleared = createAction<FlowBuilderAgentFlowPayload>(
  'flowBuilder/previewCleared',
);
/** Pede a todos os editores do agente que recarreguem o rascunho (conflito de revisão). */
export const flowBuilderDraftReloadRequested = createAction<{ agentId: string }>(
  'flowBuilder/draftReloadRequested',
);
/**
 * "Parar" sem o terminal do servidor: o painel deixa de esperar e encerra o turno neste navegador.
 * É uma action, e não só o abort do thunk, porque a aba que mostra o turno pode não ser a que abriu
 * o stream — e essa não tem o que abortar.
 */
export const flowBuilderTurnReleasedLocally = createAction<CancelFlowBuilderTurnInput>(
  'flowBuilder/turnReleasedLocally',
);

const hasLiveTurn = (state: RootState, agentId: string): boolean => {
  const turn = state.flowBuilder.agents[agentId]?.turn;
  return Boolean(turn && turn.phase !== 'finished');
};

export const fetchFlowBuilderSessions = createAsyncThunk<
  FlowBuilderSessionPage,
  FlowBuilderSessionsQuery,
  FlowBuilderThunkConfig
>(
  'flowBuilder/fetchSessions',
  async (query, { rejectWithValue }) => {
    try {
      return await flowBuilderService.listSessions(query);
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: ({ agentId }, { getState }) =>
      getState().flowBuilder.agents[agentId]?.sessions.status !== 'loading',
  },
);

export const createFlowBuilderSession = createAsyncThunk<
  FlowBuilderSessionDto,
  CreateFlowBuilderSessionInput,
  FlowBuilderThunkConfig
>('flowBuilder/createSession', async (input, { rejectWithValue }) => {
  try {
    return await flowBuilderService.createSession(input);
  } catch (error) {
    return rejectWithValue(toAgentProblem(error));
  }
});

export const fetchFlowBuilderMessages = createAsyncThunk<
  FlowBuilderMessagePage,
  FlowBuilderMessagesQuery,
  FlowBuilderThunkConfig
>('flowBuilder/fetchMessages', async (query, { rejectWithValue }) => {
  try {
    return await flowBuilderService.listMessages(query);
  } catch (error) {
    return rejectWithValue(toAgentProblem(error));
  }
});

/**
 * Cada evento vira uma action: o reducer do FlowBuilder traduz o turno, e o do agente e o das abas
 * reagem ao mesmo evento (revisão, catálogos, aba do sub-fluxo). No fim o agente é relido para o
 * cabeçalho do editor mostrar contagens e data da revisão nova.
 */
async function streamTurnIntoStore({
  agentId,
  turnId,
  request,
  signal,
  dispatch,
}: FlowBuilderTurnStreamArgs) {
  try {
    return await flowBuilderService.streamTurn({
      agentId,
      turnId,
      request,
      signal,
      onEvent: (event, eventId) =>
        dispatch(flowBuilderStreamEventReceived({ agentId, turnId, eventId, event })),
    });
  } finally {
    void dispatch(fetchNegotiationAgent({ id: agentId, refresh: true }));
  }
}

/** Conversa nova não tem sessão ainda: ela nasce aqui, com o escritório de teste escolhido. */
async function sessionIdForTurn(
  input: StartFlowBuilderTurnInput,
  dispatch: FlowBuilderDispatch,
): Promise<string> {
  if (input.sessionId) return input.sessionId;
  const session = await dispatch(
    createFlowBuilderSession({ agentId: input.agentId, ...input.newSession }),
  ).unwrap();
  return session.id;
}

export const startFlowBuilderTurn = createAsyncThunk<
  FlowBuilderTurnStreamOutcome,
  StartFlowBuilderTurnInput,
  FlowBuilderThunkConfig
>(
  'flowBuilder/startTurn',
  async (input, { dispatch, signal, rejectWithValue }) => {
    try {
      const sessionId = await sessionIdForTurn(input, dispatch);
      return await streamTurnIntoStore({
        agentId: input.agentId,
        turnId: input.turnId,
        request: {
          sessionId,
          message: input.message,
          expectedDraftRevision: input.expectedDraftRevision,
        },
        signal,
        dispatch,
      });
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  { condition: ({ agentId }, { getState }) => !hasLiveTurn(getState(), agentId) },
);

/** Reconecta ao turno vivo da sessão (`liveTurnId`): depois de recarregar a página, por exemplo. */
export const resumeFlowBuilderTurn = createAsyncThunk<
  FlowBuilderTurnStreamOutcome,
  ResumeFlowBuilderTurnInput,
  FlowBuilderThunkConfig
>(
  'flowBuilder/resumeTurn',
  async ({ agentId, turnId }, { dispatch, signal, rejectWithValue }) => {
    try {
      return await streamTurnIntoStore({ agentId, turnId, signal, dispatch });
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  { condition: ({ agentId }, { getState }) => !hasLiveTurn(getState(), agentId) },
);

export const cancelFlowBuilderTurn = createAsyncThunk<
  FlowBuilderCancelResult,
  CancelFlowBuilderTurnInput,
  FlowBuilderThunkConfig
>('flowBuilder/cancelTurn', async ({ agentId, turnId }, { rejectWithValue }) => {
  try {
    return await flowBuilderService.cancelTurn(agentId, turnId);
  } catch (error) {
    return rejectWithValue(toAgentProblem(error));
  }
});

const TURN_END_CHECK_INTERVAL_MS = 5_000;

/**
 * Pergunta ao core se o turno que acabou só neste navegador ainda vive. Com o turno vivo (ou com
 * falha que não seja 404), a resposta só volta depois do intervalo: a consulta segue "em voo" nesse
 * tempo, e os outros editores abertos do mesmo agente não disparam a deles.
 */
export const checkFlowBuilderTurnEnd = createAsyncThunk<
  FlowBuilderTurnStatusDto,
  CheckFlowBuilderTurnEndInput,
  FlowBuilderThunkConfig
>(
  'flowBuilder/checkTurnEnd',
  async ({ agentId, turnId }, { signal, rejectWithValue }) => {
    try {
      const turn = await flowBuilderService.getTurnStatus(agentId, turnId);
      if (isLiveTurnStatus(turn.status)) await esperar(TURN_END_CHECK_INTERVAL_MS, signal);
      return turn;
    } catch (error) {
      const problem = toAgentProblem(error);
      if (problem.status !== 404) await esperar(TURN_END_CHECK_INTERVAL_MS, signal);
      return rejectWithValue(problem);
    }
  },
  {
    condition: ({ agentId, turnId }, { getState }) => {
      const unconfirmed = getState().flowBuilder.agents[agentId]?.unconfirmedTurn;
      return unconfirmed?.turnId === turnId && !unconfirmed.checking;
    },
  },
);
